import { useMemo } from 'react';
import { Tabs } from 'antd';
import { useSearchParams } from 'react-router-dom';

import { useMedicalSections } from '@/entities/medical/api/medicalChecksApi';
import type { MedicalSectionKey } from '@/entities/medical/model/medicalChecksSchema';
import { medicalSectionKeyLabels } from '@/entities/medical/model/medicalChecksSchema';
import { MedRecordsTab } from './MedRecordsTab';
import { ReferralsTab } from './ReferralsTab';
import { MyExamsTab } from './MyExamsTab';
import { PlanScheduleTab } from './PlanScheduleTab';
import { PageSpinner } from '@/shared/ui/PageSpinner';

export function Medical() {
  const [params, setParams] = useSearchParams();
  const tabFromUrl = params.get('tab') ?? '';

  const { data: sections, isPending: sectionsPending } = useMedicalSections();
  const allowedKeys = useMemo(() => new Set(sections?.map((s) => s.key)), [sections]);

  const visibleTabs = useMemo(() => {
    const allTabs = [
      {
        key: 'my_exams',
        sectionKey: 'my_exams' as MedicalSectionKey,
        label: medicalSectionKeyLabels.my_exams,
        children: <MyExamsTab />,
      },
      {
        key: 'employees',
        sectionKey: 'employees_medical_table' as MedicalSectionKey,
        label: medicalSectionKeyLabels.employees_medical_table,
        children: <MedRecordsTab />,
      },
      {
        key: 'plan',
        sectionKey: 'plan_schedule' as MedicalSectionKey,
        label: medicalSectionKeyLabels.plan_schedule,
        children: <PlanScheduleTab />,
      },
      {
        key: 'referrals',
        sectionKey: 'create_medical_referral' as MedicalSectionKey,
        label: medicalSectionKeyLabels.create_medical_referral,
        children: <ReferralsTab />,
      },
    ];

    if (!sections) return [];
    return allTabs.filter((t) => !t.sectionKey || allowedKeys.has(t.sectionKey));
  }, [sections, allowedKeys]);

  const firstTabKey = visibleTabs[0]?.key ?? 'employees';

  const activeKey = useMemo(() => {
    if (tabFromUrl && visibleTabs.some((t) => t.key === tabFromUrl)) return tabFromUrl;
    return firstTabKey;
  }, [tabFromUrl, visibleTabs, firstTabKey]);

  const handleTabChange = (key: string) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', key);
      return next;
    });
  };

  if (sectionsPending) {
    return <PageSpinner />;
  }

  return <Tabs activeKey={activeKey} onChange={handleTabChange} items={visibleTabs} />;
}
